import { type Contract, sharedContractWork } from "./model";
import { useI18n } from "../../lib/i18n";
import { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { Clock, Loader2, StopCircle, Play, Send, ListChecks, CheckSquare, Square, Trash2 } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { ContractChecklist } from "../../components/ContractChecklist";
import { profileKey } from "../../lib/profileSession";
import { WorkReports } from "../../components/WorkReports";
import { WorkHistory } from "../../components/WorkHistory";
import { WorkLayout } from "../../components/WorkLayout";
import { JobContractWork } from "./JobContractWork";

/* Batch items 6 & 7: time tracking (worker clocks in/out) + progress log. */
function ContractWorkLog({ contract: c, mobile = false }: { contract: Contract; mobile?: boolean }) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  const [tl, setTl] = useState<{ logs: any[]; totalMinutes: number; openLogId: string | null } | null>(null);
  const [entries, setEntries] = useState<any[]>([]);
  const [note, setNote] = useState("");
  const [logType, setLogType] = useState<"update" | "milestone" | "issue">("update");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isProvider = c.role === "provider";
  const showTimeTracking = c.sourceType !== "project" && c.sourceType !== "business" && c.sourceType !== "rfq_items";
  const hasWorkStarted = c.status === "funded" || c.status === "completed";
  const canPost = c.status === "funded";

  async function load() {
    try {
      const [timeLogs, progress] = await Promise.all([
        showTimeTracking ? api.contractTimeLogs(c.id) : Promise.resolve({ logs: [], totalMinutes: 0, openLogId: null }),
        api.contractProgress(c.id),
      ]);
      setTl(timeLogs); setEntries(progress);
    } catch { /* */ }
  }
  useEffect(() => { if (show || mobile) load(); /* eslint-disable-next-line */ }, [show, mobile, c.id]);

  function fmt(min: number) { const h = Math.floor(min / 60), m = min % 60; return h ? `${h}h ${m}m` : `${m}m`; }

  async function clockIn() { setBusy(true); setErr(null); try { await api.contractTimeIn(c.id); await load(); } catch (e: any) { setErr(e?.message || "Could not clock in."); } finally { setBusy(false); } }
  async function clockOut() { setBusy(true); setErr(null); try { await api.contractTimeOut(c.id); await load(); } catch (e: any) { setErr(e?.message || "Could not clock out."); } finally { setBusy(false); } }
  async function postProgress() {
    if (note.trim().length < 2) return;
    setBusy(true); setErr(null);
    try { await api.addContractProgress(c.id, note.trim(), logType); setNote(""); setLogType("update"); await load(); }
    catch (e: any) { setErr(e?.message || "Could not post update."); } finally { setBusy(false); }
  }

  const clockedIn = !!tl?.openLogId;
  const typeChip: Record<string, string> = { update: "bg-navy-50 text-navy-600", milestone: "bg-brand-green/10 text-brand-greendark", issue: "bg-brand-red/10 text-brand-reddark" };

  return (
    <div className="mt-3 pt-3 border-t border-line">
      {mobile ? <h3>{t("contracts.detail.logs")}</h3> : <button onClick={() => setShow(!show)} className="text-xs font-semibold text-navy flex items-center gap-1.5">
        <Clock className="w-4 h-4" />{showTimeTracking ? "Time & progress log" : "Project progress log"} {show ? "▲" : "▼"}
      </button>}
      {(show || mobile) && !hasWorkStarted && c.sourceType !== "project" && (
        <div className="mt-3 rounded-lg bg-navy-50/60 border border-line p-3 text-xs text-mut">Time tracking and progress updates unlock once the client funds the contract into escrow. This section stays read-only until then.</div>
      )}
      {(show || mobile) && tl && (hasWorkStarted || c.sourceType === "project") && (
        <div className={`mt-3 grid gap-4 ${showTimeTracking ? "md:grid-cols-2" : ""}`}>
          {/* Time tracking */}
          {showTimeTracking && <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-mut">Time logged</span>
              <span className="text-xs font-bold text-navy">{fmt(tl.totalMinutes)}</span>
            </div>
            {isProvider && canPost ? (
              <div className="mb-2">
                {clockedIn ? (
                  <button onClick={clockOut} disabled={busy} className="btn-red h-8 px-3 text-xs disabled:opacity-50">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <StopCircle className="w-3.5 h-3.5" />}Clock out</button>
                ) : (
                  <button onClick={clockIn} disabled={busy} className="btn-outline h-8 px-3 text-xs disabled:opacity-50">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}Clock in</button>
                )}
                {clockedIn && <span className="text-[11px] text-brand-green ml-2">● Currently clocked in</span>}
              </div>
            ) : (
              <p className="text-[11px] text-mut mb-2">Hours logged by the worker on this contract.</p>
            )}
            <div className="space-y-1 max-h-40 overflow-auto">
              {tl.logs.length === 0 && <div className="text-xs text-mut">No time logged yet.</div>}
              {tl.logs.map((l) => (
                <div key={l.id} className="text-xs border border-line rounded-lg p-2 flex items-center justify-between">
                  <span className="text-navy-700">{new Date(l.clockIn).toLocaleDateString()} {new Date(l.clockIn).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="font-semibold text-navy">{l.clockOut ? fmt(l.minutes || 0) : "in progress…"}</span>
                </div>
              ))}
            </div>
          </div>}
          {/* Progress log */}
          <div>
            <div className="text-xs font-semibold text-mut mb-1">Progress updates</div>
            <p className="text-[11px] text-mut mb-1.5">Log milestones, status notes or issues here so the client and provider have a shared, timestamped record of the work.</p>
            <div className="space-y-1.5 max-h-40 overflow-auto">
              {entries.length === 0 && <div className="text-xs text-mut">No updates posted yet.</div>}
              {entries.map((e) => (
                <div key={e.id} className="text-sm border border-line rounded-lg p-2">
                  <span className={`chip text-[10px] mr-1 capitalize ${typeChip[e.logType] || typeChip.update}`}>{e.logType}</span>
                  <span className="chip bg-navy-50 text-navy-600 text-[10px] mr-1 capitalize">{e.byRole}</span>
                  <span className="text-navy-700">{e.note}</span>
                </div>
              ))}
            </div>
            {canPost && <div className="mt-2 space-y-1.5">
              <div className="flex gap-1.5">
                <select className="field h-8 text-xs w-28" value={logType} onChange={(e) => setLogType(e.target.value as any)}>
                  <option value="update">Update</option>
                  <option value="milestone">{t("transaction.milestone")}</option>
                  <option value="issue">Issue</option>
                </select>
                <input className="field h-8 text-sm" placeholder="Post a progress update…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postProgress()} />
                <button type="button" aria-label="Post progress update" onClick={postProgress} disabled={busy} className="btn-outline h-8 px-2.5 text-xs disabled:opacity-50"><Send aria-hidden="true" className="w-3.5 h-3.5" /></button>
              </div>
            </div>}
          </div>
        </div>
      )}
      {err && <p className="text-xs text-brand-red mt-1">{err}</p>}
    </div>
  );
}

/* Selected sources share reports and checklist; excluded sources retain their existing tools. */
export function ContractWorkPanel({ contract, mobile = false }: { contract: Contract; mobile?: boolean }) {
  const { user } = useAuth();
  if (contract.sourceType === "job") return <JobContractWork key={`${profileKey(user)}:${contract.sourceId}`} contract={contract} />;
  const shared = sharedContractWork(contract);
  return shared ? <SharedContractWork key={`${profileKey(user)}:${contract.id}`} contract={contract} />
    : <><ContractProgress contract={contract} mobile={mobile} /><ContractWorkLog contract={contract} mobile={mobile} /></>;
}
function SharedContractWork({ contract }: { contract: Contract }) {
  const [denied, setDenied] = useState(false);
  const clearAccess = useCallback(() => setDenied(true), []);
  if (denied) return <p role="alert" className="text-sm text-brand-red">This work record is no longer available in the selected account or profile.</p>;
  return <WorkLayout checklist={<ContractChecklist contractId={contract.id} status={contract.status} onAccessLost={clearAccess} />}
    history={<WorkHistory kind="contract" id={contract.id} refreshKey={contract.status} onAccessLost={clearAccess} />}>
    <WorkReports kind="contract" id={contract.id} writable={contract.status === "funded"} refreshKey={contract.status} onAccessLost={clearAccess} />
  </WorkLayout>;
}

function ContractProgress({ contract: c, mobile = false, legacyChecklist = true }: { contract: Contract; mobile?: boolean; legacyChecklist?: boolean }) {
  const { t } = useI18n();
  const [items, setItems] = useState<any[]>([]);
  const [checkins, setCheckins] = useState<any[]>([]);
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  async function load() {
    try { const [it, ci] = await Promise.all([legacyChecklist ? api.contractChecklist(c.id) : Promise.resolve([]), api.contractCheckins(c.id)]); setItems(it); setCheckins(ci); } catch { /* */ }
  }
  useEffect(() => { if (show || mobile) load(); /* eslint-disable-next-line */ }, [show, mobile, c.id]);

  const doneCount = items.filter((i) => i.done).length;
  const isProvider = c.role === "provider"; // [QA C1] only the provider edits deliverables/check-ins
  const canEdit = isProvider && (c.sourceType === "project" || (c.sourceType === "service" || c.sourceType === "business" || c.sourceType === "rfq_items") ? c.status === "funded" : c.status === "pending_funding" || c.status === "funded");

  async function addItem() { if (label.trim().length < 2) return; setBusy(true); try { await api.addChecklistItem(c.id, label.trim()); setLabel(""); await load(); } catch { /* */ } finally { setBusy(false); } }
  async function toggle(id: string) { try { await api.toggleChecklistItem(c.id, id); await load(); } catch { /* */ } }
  async function del(id: string) { try { await api.deleteChecklistItem(c.id, id); await load(); } catch { /* */ } }
  async function addNote() { if (note.trim().length < 2) return; setBusy(true); try { await api.addCheckin(c.id, note.trim()); setNote(""); await load(); } catch { /* */ } finally { setBusy(false); } }

  return (
    <div className="mt-3 pt-3 border-t border-line">
      {mobile ? <h3>{legacyChecklist ? t("contracts.detail.checklist") : "Progress check-ins"}</h3> : <button onClick={() => setShow(!show)} className="text-xs font-semibold text-navy flex items-center gap-1.5">
        <ListChecks className="w-4 h-4" />{legacyChecklist ? "Checklist & check-ins" : "Progress check-ins"} {show ? "▲" : "▼"}
      </button>}
      {(show || mobile) && (
        <div className={`mt-3 grid ${legacyChecklist ? "md:grid-cols-2" : ""} gap-4`}>
          {/* Checklist */}
          {legacyChecklist && <div>
            <div className="text-xs font-semibold text-mut mb-1">Deliverables ({doneCount}/{items.length})</div>
            <div className="space-y-1">
              {items.map((i) => (
                <div key={i.id} className="flex items-center gap-2 text-sm group">
                  {canEdit
                    ? <button type="button" aria-label={`Deliverable completion: ${i.label}`} aria-pressed={Boolean(i.done)} onClick={() => toggle(i.id)} className="text-navy">{i.done ? <CheckSquare aria-hidden="true" className="w-4 h-4 text-brand-green" /> : <Square aria-hidden="true" className="w-4 h-4 text-mut" />}</button>
                    : <span className="text-navy">{i.done ? <CheckSquare className="w-4 h-4 text-brand-green" /> : <Square className="w-4 h-4 text-mut" />}</span>}
                  <span className={`flex-1 ${i.done ? "line-through text-mut" : "text-navy"}`}>{i.label}</span>
                  {canEdit && <button onClick={() => del(i.id)} className="opacity-0 group-hover:opacity-100 text-brand-red" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              ))}
            </div>
            {canEdit ? (
              <div className="flex gap-1.5 mt-2">
                <input className="field h-8 text-sm" placeholder="Add a deliverable…" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} />
                <button onClick={addItem} disabled={busy} className="btn-outline h-8 px-2.5 text-xs disabled:opacity-50">Add</button>
              </div>
            ) : (
              <p className="text-[11px] text-mut mt-2">{items.length ? "The provider updates these deliverables." : "The provider will add deliverables here."}</p>
            )}
          </div>}
          {/* Check-ins */}
          <div>
            {legacyChecklist && <div className="text-xs font-semibold text-mut mb-1">Progress check-ins</div>}
            <div className="space-y-1.5 max-h-40 overflow-auto">
              {checkins.length === 0 && <div className="text-xs text-mut">No check-ins yet.</div>}
              {checkins.map((ci) => (
                <div key={ci.id} className="text-sm border border-line rounded-lg p-2">
                  <span className="chip bg-navy-50 text-navy-600 text-[10px] mr-1 capitalize">{ci.byRole}</span>
                  <span className="text-navy-700">{ci.note}</span>
                </div>
              ))}
            </div>
            {canEdit ? (
              <div className="flex gap-1.5 mt-2">
                <input className="field h-8 text-sm" placeholder="Post an update…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} />
                <button type="button" aria-label="Post check-in" onClick={addNote} disabled={busy} className="btn-outline h-8 px-2.5 text-xs disabled:opacity-50"><Send aria-hidden="true" className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <p className="text-[11px] text-mut mt-2">The provider posts progress updates here.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
